import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {useCourseTheme} from 'course/contexts/ThemeContext';
import {useField, type FormikHelpers} from 'formik';
import {
  useId,
  useMemo,
  type ChangeEventHandler,
  type CSSProperties,
  type InputHTMLAttributes,
  type KeyboardEventHandler,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import {
  DndContext,
  useDraggable,
  useDroppable,
  type DragEndEvent,
} from '@dnd-kit/core';
import {CSS} from '@dnd-kit/utilities';
import {MdOutlineDragIndicator} from 'react-icons/md';
import {
  computeRankDrop,
  rankValuesToSlotIndices,
  slotIndicesToRankValues,
  type RankDropTarget,
  type RankOption,
} from './rankUtils';
import { DateOfBirth as DateOfBirthValueType } from 'course/application/firebase/models/formData';

// inputs that correspond to survey components: (see SurveyComponent.tsx)
export const TextInput = ({
  name,
  label,
  description = '',
  placeholder = '',
  ...inputProps
}: {
  name: string;
  label: string;
  description?: ReactNode;
  placeholder?: string;
} & InputHTMLAttributes<HTMLInputElement>) => {
  // creates a text input for short response
  const [field, meta] = useField({name, ...inputProps, type: 'text'});

  const autoGeneratedId = useId();
  const inputId = inputProps.id ?? `${name}-${autoGeneratedId}`;
  const errorId = `${inputId}-error`;
  const descriptionId = `${inputId}-description`;
  const shouldDisplayError = meta.touched && !!meta.error;

  const describedBy =
    [
      description ? descriptionId : undefined,
      shouldDisplayError ? errorId : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className="question-spacing">
      <label className="form-label" htmlFor={inputId}>
        {label}
      </label>
      {description && (
        <div className="description" id={descriptionId}>
          {description}
        </div>
      )}

      <input
        {...field}
        {...inputProps}
        placeholder={placeholder}
        className="about-text-input dark-placeholder"
        id={inputId}
        aria-describedby={describedBy}
        aria-invalid={shouldDisplayError || undefined}
        aria-errormessage={shouldDisplayError ? errorId : undefined}
      />
      {shouldDisplayError && (
        <div className="error" id={errorId} aria-live="polite">
          {meta.error}
        </div>
      )}
    </div>
  );
};

export const TextAreaInput = ({
  name,
  label,
  description = '',
  ...inputProps
}: {
  name: string;
  label: string;
  description?: ReactNode;
} & TextareaHTMLAttributes<HTMLTextAreaElement>) => {
  // creates a text area input for longer text
  const [field, meta] = useField({name, ...inputProps});

  const autoGeneratedId = useId();
  const inputId = inputProps.id ?? `${name}-${autoGeneratedId}`;
  const errorId = `${inputId}-error`;
  const descriptionId = `${inputId}-description`;
  const shouldDisplayError = meta.touched && !!meta.error;

  const describedBy =
    [
      description ? descriptionId : undefined,
      shouldDisplayError ? errorId : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className="question-spacing">
      <label className="form-label" htmlFor={inputId}>
        {label}
      </label>
      {description && (
        <div className="description" id={descriptionId}>
          {description}
        </div>
      )}
      <textarea
        {...field}
        {...{...{rows: 10}, ...inputProps}}
        className="text-area-input dark-placeholder"
        id={inputId}
        aria-describedby={describedBy}
        aria-invalid={shouldDisplayError || undefined}
        aria-errormessage={shouldDisplayError ? errorId : undefined}
      />
      {shouldDisplayError && (
        <div className="error" id={errorId} aria-live="polite">
          {meta.error}
        </div>
      )}
    </div>
  );
};

export const DescriptionField = ({
  label,
  description,
}: {
  label: string;
  description?: ReactNode;
}) => {
  return (
    <div>
      <div className="form-label">{label}</div>
      {description && <div className="description">{description}</div>}
    </div>
  );
};

export const LikertScale = ({
  name,
  label,
}: {
  name: string;
  label: ReactNode;
}) => {
  const t = useTranslateFunction('miscUI');
  // automatically creates a likert scale with 5 options
  // values are 1-5 (strings)
  // you can only select one at a time
  const [field, meta, {setValue}] = useField<string>({name});
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const shouldDisplayError = meta.touched && !!meta.error;

  const radioProps = (value: string) => ({
    type: 'radio' as const,
    name,
    value,
    checked: field.value === value,
    onChange: () => setValue(value),
    onBlur: field.onBlur,
    ...(shouldDisplayError && {
      'aria-describedby': errorId,
      'aria-errormessage': errorId,
    }),
  });

  return (
    <fieldset
      className="question-spacing"
      id={inputId}
      aria-invalid={shouldDisplayError || undefined}
    >
      <legend className="form-label">{label}</legend>
      <div className="likert-scale">
        <label className="likert-option">
          <span className="likert-stacked-label">
            {t('Strongly Disagree')}
          </span>
          <input {...radioProps('1')} />
        </label>
        <label className="likert-option">
          {t('Disagree')}
          <input {...radioProps('2')} />
        </label>
        <label className="likert-option">
          {t('Neutral')}
          <input {...radioProps('3')} />
        </label>
        <label className="likert-option">
          {t('Agree')}
          <input {...radioProps('4')} />
        </label>
        <label className="likert-option">
          <span className="likert-stacked-label">
            {t('Strongly Agree')}
          </span>
          <input {...radioProps('5')} />
        </label>
      </div>
      {shouldDisplayError && (
        <div className="error" id={errorId} aria-live="polite">
          {meta.error}
        </div>
      )}
    </fieldset>
  );
};

/**
 * A dropdown input.
 *
 * Children become options in a select dropdown; you can select only one at a
 * time. This is a lot like Select below, except that we expect that "children"
 * is an array of objects with "value" and "label" keys. We map over the
 * children to create the options.
 */
export const Dropdown = ({
  name,
  label,
  options,
  description,
  ...selectProps
}: {
  name: string;
  label: string;
  options: {value: string; label: string}[];
  description?: ReactNode;
} & SelectHTMLAttributes<HTMLSelectElement>) => {
  const [field, meta] = useField({name, ...selectProps});
  const autoGeneratedId = useId();
  const inputId = selectProps.id ?? `${name}-${autoGeneratedId}`;
  const errorId = `${inputId}-error`;
  const descriptionId = `${inputId}-description`;
  const shouldDisplayError = meta.touched && !!meta.error;

  const describedBy =
    [
      description ? descriptionId : undefined,
      shouldDisplayError ? errorId : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className="question-spacing">
      <label className="form-label" htmlFor={inputId}>
        {label}
      </label>
      {description && (
        <div className="description" id={descriptionId}>
          {description}
        </div>
      )}

      <select
        {...field}
        {...selectProps}
        className="select-input"
        id={inputId}
        aria-describedby={describedBy}
        aria-invalid={shouldDisplayError || undefined}
        aria-errormessage={shouldDisplayError ? errorId : undefined}
      >
        {/* default option */}
        {/* eslint-disable-next-line jsx-a11y/control-has-associated-label -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue */}
        <option value=""></option>
        {options.map((option, index) => {
          return (
            // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
            <option key={index} value={option.value}>
              {option.label}
            </option>
          );
        })}
      </select>
      {shouldDisplayError && (
        <div className="error" id={errorId} aria-live="polite">
          {meta.error}
        </div>
      )}
    </div>
  );
};

/**
 * A set of checkboxes that can be used to represent a list of values.
 *
 * In the final form, the value is an array of the value fields (see `options`)
 * of the checkboxes that are checked.
 */
export const MultiCheckbox = ({
  name,
  options,
  description,
  label,
  style = {},
  allowOther = false,
  otherLabel = 'Other',
}: {
  name: string;
  options: {label: string; value: string}[];
  label: ReactNode;
  description?: ReactNode;
  style?: CSSProperties;
  /**
   * When true, render an extra "Other" checkbox with a free-text box. The typed
   * value is stored as an additional entry in the field's array (any value not
   * matching a predefined option is treated as the "Other" write-in). Defaults
   * to false, so existing consumers are unaffected.
   */
  allowOther?: boolean;
  /** Label for the "Other" checkbox. Defaults to "Other". */
  otherLabel?: string;
}) => {
  // children become checkboxes which are displayed vertically; you can select multiple at once (or none!)
  const [field, meta, helpers] = useField<string[]>({name});
  // Guard against legacy/mismatched saved data: a field whose type was changed
  // (e.g. dropdown → multicheckbox) can have a scalar value where an array is
  // now expected. `?? []` only covers null/undefined, so coerce anything
  // non-array to an empty array to avoid `.find`/`.includes` throwing.
  const currentValue = Array.isArray(field.value) ? field.value : [];
  const fixedValues = options.map(option => option.value);
  // The "Other" write-in is any selected value that is not a predefined option.
  // An empty string represents "Other checked but not yet filled in".
  const otherEntry = currentValue.find(value => !fixedValues.includes(value));
  const isOtherChecked = otherEntry !== undefined;
  const autoGeneratedId = useId();
  const baseId = `${name}-${autoGeneratedId}`;
  const descriptionId = `${baseId}-description`;
  const errorId = `${baseId}-error`;
  const shouldDisplayError = meta.touched && !!meta.error;

  const displayedDescription = description ?? 'Select all that apply';
  return (
    <fieldset
      className="question-spacing"
      style={style}
      id={baseId}
      aria-describedby={descriptionId}
      aria-invalid={shouldDisplayError || undefined}
      aria-errormessage={shouldDisplayError ? errorId : undefined}
    >
      <legend className="form-label">{label}</legend>
      {displayedDescription && (
        <div className="description" id={descriptionId}>
          {displayedDescription}
        </div>
      )}
      {options.map((option, index) => {
        return (
          <Checkbox
            // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
            key={index}
            name={name}
            label={option.label}
            value={option.value}
            aria-describedby={descriptionId}
            aria-invalid={shouldDisplayError || undefined}
            aria-errormessage={shouldDisplayError ? errorId : undefined}
          />
        );
      })}
      {allowOther && (
        <div className="checkbox">
          <label className="form-label">
            <input
              type="checkbox"
              checked={isOtherChecked}
              onChange={e => {
                if (e.target.checked) {
                  // Add an empty "Other" entry (filled in via the text box).
                  helpers.setValue([...currentValue, '']);
                } else {
                  // Drop any non-predefined entry.
                  helpers.setValue(
                    currentValue.filter(value => fixedValues.includes(value)),
                  );
                }
                helpers.setTouched(true);
              }}
            />
            {otherLabel}
          </label>
          {isOtherChecked && (
            <input
              type="text"
              className="form-control"
              style={{maxWidth: 400, marginTop: 4}}
              value={otherEntry ?? ''}
              placeholder="Please specify"
              onChange={e => {
                const fixed = currentValue.filter(value =>
                  fixedValues.includes(value),
                );
                helpers.setValue([...fixed, e.target.value]);
              }}
            />
          )}
        </div>
      )}
      {shouldDisplayError && (
        <div className="error" id={errorId} aria-live="polite">
          {meta.error}
        </div>
      )}
    </fieldset>
  );
};

const Checkbox = ({
  name,
  label,
  value,
  ...inputProps
}: {
  name: string;
  label: string;
  value: string;
} & InputHTMLAttributes<HTMLInputElement>) => {
  const [field] = useField({name, type: 'checkbox', value, ...inputProps});
  const autoGeneratedId = useId();
  const inputId = inputProps.id ?? `${name}-${autoGeneratedId}`;
  return (
    <div className="checkbox">
      <label className="form-label" htmlFor={inputId}>
        <input {...inputProps} {...field} type="checkbox" id={inputId} />
        {label}
      </label>
    </div>
  );
};

/**
 * A single checkbox whose value in the final form is a boolean.
 */
export const BooleanCheckbox = ({
  name,
  label,
  description,
  ...inputProps
}: {
  name: string;
  label: ReactNode;
  description?: ReactNode;
} & InputHTMLAttributes<HTMLInputElement>) => {
  const [field, meta, {setValue}] = useField<boolean>({
    name,
    type: 'checkbox',
    ...inputProps,
  });
  const autoGeneratedId = useId();
  const inputId = inputProps.id ?? `${name}-${autoGeneratedId}`;
  const descriptionId = `${inputId}-description`;
  const errorId = `${inputId}-error`;
  const shouldDisplayError = meta.touched && !!meta.error;

  const describedBy =
    [
      description ? descriptionId : undefined,
      shouldDisplayError ? errorId : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className="checkbox">
      <label className="form-label" htmlFor={inputId}>
        <input
          {...inputProps}
          name={field.name}
          checked={field.value}
          onChange={e => {
            field.onChange(e);
            setValue(e.target.checked);
          }}
          onBlur={field.onBlur}
          type="checkbox"
          id={inputId}
          aria-describedby={describedBy}
          aria-invalid={shouldDisplayError || undefined}
          aria-errormessage={shouldDisplayError ? errorId : undefined}
        />
        {label}
      </label>
      {description && (
        <div id={descriptionId} className="description">
          {description}
        </div>
      )}
      {shouldDisplayError && (
        <div id={errorId} className="error" aria-live="polite">
          {meta.error}
        </div>
      )}
    </div>
  );
};

export const MultipleChoice = ({
  name,
  label,
  options,
  description,
}: {
  name: string;
  label: string;
  options: {value?: string; label: string}[];
  description?: ReactNode;
}) => {
  const [, meta] = useField({name});
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const descriptionId = `${inputId}-description`;
  const shouldDisplayError = meta.touched && !!meta.error;

  const describedBy =
    [
      description ? descriptionId : undefined,
      shouldDisplayError ? errorId : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;
  return (
    <fieldset
      className="question-spacing"
      id={inputId}
      aria-describedby={describedBy}
      aria-invalid={shouldDisplayError || undefined}
      aria-errormessage={shouldDisplayError ? errorId : undefined}
    >
      <legend className="form-label">{label}</legend>
      {description && (
        <div id={descriptionId} className="description">
          {description}
        </div>
      )}
      {options.map((option, index) => {
        return (
          <RadioButton
            // eslint-disable-next-line @eslint-react/no-array-index-key -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
            key={index}
            name={name}
            value={option.value}
            label={option.label}
          />
        );
      })}
      {meta.touched && meta.error && (
        <div id={errorId} className="error" aria-live="polite">
          {meta.error}
        </div>
      )}
    </fieldset>
  );
};

const RadioButton = ({
  name,
  value,
  label,
}: {
  name: string;
  value: string;
  label: string;
}) => {
  const [field] = useField({type: 'radio', name, value});
  return (
    <div className="radio">
      <label>
        <input {...field} type="radio" />
        {label}
      </label>
    </div>
  );
};

/**
 * A linear-scale rating input: a horizontal row of radio buttons numbered from
 * `min` to `max`, with optional labels anchoring each end (e.g. "Good" … "Bad").
 * The selected value is stored as its string representation.
 */
export const LinearScaleInput = ({
  name,
  label,
  min,
  max,
  minLabel,
  maxLabel,
  description,
}: {
  name: string;
  label: ReactNode;
  min: number;
  max: number;
  minLabel?: string;
  maxLabel?: string;
  description?: ReactNode;
}) => {
  const [field, meta, helpers] = useField<string>({name});
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const descriptionId = `${inputId}-description`;
  const shouldDisplayError = meta.touched && !!meta.error;

  const values = Array.from(
    {length: Math.max(0, max - min + 1)},
    (_, index) => min + index,
  );

  return (
    <fieldset
      className="question-spacing"
      id={inputId}
      aria-describedby={description ? descriptionId : undefined}
      aria-invalid={shouldDisplayError || undefined}
      aria-errormessage={shouldDisplayError ? errorId : undefined}
    >
      <legend className="form-label">{label}</legend>
      {description && (
        <div id={descriptionId} className="description">
          {description}
        </div>
      )}
      <div
        className="d-flex align-items-center flex-wrap"
        style={{gap: 16, marginTop: 8}}
      >
        {minLabel && <span>{minLabel}</span>}
        {values.map(value => {
          const valueStr = String(value);
          return (
            <label
              key={value}
              className="d-flex flex-column align-items-center mb-0"
              style={{gap: 4, cursor: 'pointer'}}
            >
              <span>{value}</span>
              <input
                type="radio"
                name={name}
                value={valueStr}
                checked={field.value === valueStr}
                onChange={() => {
                  helpers.setValue(valueStr);
                  helpers.setTouched(true);
                }}
              />
            </label>
          );
        })}
        {maxLabel && <span>{maxLabel}</span>}
      </div>
      {shouldDisplayError && (
        <div id={errorId} className="error" aria-live="polite">
          {meta.error}
        </div>
      )}
    </fieldset>
  );
};

const RankChip = ({
  option,
  optionIndex,
}: {
  option: RankOption;
  optionIndex: number;
}) => {
  const {attributes, listeners, setNodeRef, transform, isDragging} =
    useDraggable({id: `rank-opt-${optionIndex}`});
  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rank-chip"
      {...listeners}
      {...attributes}
    >
      <MdOutlineDragIndicator aria-hidden="true" />
      <span>{option.label || option.value}</span>
    </div>
  );
};

const RankBank = ({
  label,
  emptyText,
  options,
  optionIndices,
}: {
  label: string;
  emptyText: string;
  options: RankOption[];
  optionIndices: number[];
}) => {
  const {setNodeRef, isOver} = useDroppable({id: 'rank-bank'});
  return (
    <div className="rank-bank">
      <div className="rank-bank-label">{label}</div>
      <div
        ref={setNodeRef}
        className="rank-bank-drop"
        style={{outline: isOver ? '2px dashed #888' : undefined}}
      >
        {optionIndices.length === 0 ? (
          <div className="rank-empty">{emptyText}</div>
        ) : (
          optionIndices.map(optionIndex => (
            <RankChip
              key={optionIndex}
              option={options[optionIndex]}
              optionIndex={optionIndex}
            />
          ))
        )}
      </div>
    </div>
  );
};

const RankSlot = ({
  slot,
  option,
  optionIndex,
}: {
  slot: number;
  option: RankOption | null;
  optionIndex: number | null;
}) => {
  const {setNodeRef, isOver} = useDroppable({id: `rank-slot-${slot}`});
  return (
    <div className="rank-slot">
      <span className="rank-slot-number">{slot + 1}</span>
      <div
        ref={setNodeRef}
        className="rank-slot-drop"
        style={{outline: isOver ? '2px dashed #888' : undefined}}
      >
        {option && optionIndex !== null ? (
          <RankChip option={option} optionIndex={optionIndex} />
        ) : (
          <span className="rank-slot-placeholder" />
        )}
      </div>
    </div>
  );
};

/**
 * A drag-and-drop ranking input.
 *
 * Renders a "bank" of unranked options alongside a column of numbered slots
 * (one slot per option). The user drags each option from the bank into a slot
 * to rank it; dropping onto an occupied slot swaps the two. The stored value is
 * an array, slot order, of the ranked option `value`s ('' for an empty slot).
 * `topLabel` / `bottomLabel` annotate the ends of the ranking (e.g. "Most
 * Useful" / "Least Useful").
 */
export const RankInput = ({
  name,
  label,
  description,
  options = [],
  topLabel,
  bottomLabel,
}: {
  name: string;
  label: ReactNode;
  description?: ReactNode;
  options?: RankOption[];
  topLabel?: string;
  bottomLabel?: string;
}) => {
  const t = useTranslateFunction('miscUI');
  const [field, meta, {setValue, setTouched}] = useField<string[]>({name});
  const baseId = useId();
  const errorId = `${baseId}-error`;
  const descriptionId = `${baseId}-description`;
  const shouldDisplayError = meta.touched && !!meta.error;

  const slotIndices = useMemo(
    () => rankValuesToSlotIndices(field.value, options),
    [field.value, options],
  );
  const placedOptionIndices = new Set(
    slotIndices.filter((index): index is number => index !== null),
  );
  const bankOptionIndices = options
    .map((_, index) => index)
    .filter(index => !placedOptionIndices.has(index));

  const commitSlots = (nextSlots: (number | null)[]) => {
    void setTouched(true);
    void setValue(slotIndicesToRankValues(nextSlots, options));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    if (!over) return;
    const optionIndex = Number(String(active.id).replace('rank-opt-', ''));
    if (Number.isNaN(optionIndex)) return;

    let target: RankDropTarget;
    if (over.id === 'rank-bank') {
      target = {type: 'bank'};
    } else {
      const toSlot = Number(String(over.id).replace('rank-slot-', ''));
      if (Number.isNaN(toSlot)) return;
      target = {type: 'slot', slot: toSlot};
    }
    commitSlots(computeRankDrop({slotIndices, optionIndex, target}));
  };

  return (
    <fieldset
      className="question-spacing"
      id={baseId}
      aria-invalid={shouldDisplayError || undefined}
      aria-errormessage={shouldDisplayError ? errorId : undefined}
      aria-describedby={description ? descriptionId : undefined}
    >
      <legend className="form-label">{label}</legend>
      {description && (
        <div className="description" id={descriptionId}>
          {description}
        </div>
      )}
      <DndContext onDragEnd={handleDragEnd}>
        <div className="rank-input">
          <RankBank
            label={t('Options', {forceString: true})}
            emptyText={t('All options ranked', {forceString: true})}
            options={options}
            optionIndices={bankOptionIndices}
          />
          <div className="rank-slots">
            {topLabel && <div className="rank-end-label">{topLabel}</div>}
            {slotIndices.map((optionIndex, slot) => (
              <RankSlot
                // eslint-disable-next-line @eslint-react/no-array-index-key -- slots are positional and fixed-length
                key={slot}
                slot={slot}
                option={optionIndex === null ? null : options[optionIndex]}
                optionIndex={optionIndex}
              />
            ))}
            {bottomLabel && (
              <div className="rank-end-label">{bottomLabel}</div>
            )}
          </div>
        </div>
      </DndContext>
      {shouldDisplayError && (
        <div className="error" id={errorId} aria-live="polite">
          {meta.error}
        </div>
      )}
    </fieldset>
  );
};

// --------------------------------------------
// below are inputs that do not correspond to survey components

/**
 * This component is NOT compatible with the FormixFormWithData component as it
 * requires passing <option> elements as children.
 */
export const SelectInput = ({
  name,
  label,
  description,
  footer,
  children,
  onChange,
  variant = 'filled',
  isLabelHidden = false,
  ...selectProps
}: {
  name: string;
  label: string;
  description?: ReactNode;
  /** Rendered directly below the select. Omitted renders nothing. */
  footer?: ReactNode;
  children: ReactNode;
  /** `'filled'` (course color) or `'outline'` (white). Defaults to filled. */
  variant?: 'filled' | 'outline';
  /** Hide the label on screen but keep it for screen readers. */
  isLabelHidden?: boolean;
} & SelectHTMLAttributes<HTMLSelectElement>) => {
  const [field, meta] = useField({name, ...selectProps});
  const {buttonColorPrimary} = useCourseTheme();
  const autoGeneratedId = useId();
  const inputId = selectProps.id ?? `${name}-${autoGeneratedId}`;
  const descriptionId = `${inputId}-description`;
  const errorId = `${inputId}-error`;
  const shouldDisplayError = meta.touched && !!meta.error;

  const describedBy =
    [
      description ? descriptionId : undefined,
      shouldDisplayError ? errorId : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  const selectStyle: CSSProperties =
    variant === 'filled'
      ? {...selectProps?.style, backgroundColor: buttonColorPrimary}
      : {...selectProps?.style};

  const handleChange: ChangeEventHandler<HTMLSelectElement> = event => {
    field.onChange(event);
    onChange?.(event);
  };

  return (
    <div className="question-spacing">
      <label
        className={
          isLabelHidden ? 'form-label visually-hidden' : 'form-label'
        }
        htmlFor={inputId}
      >
        {label}
      </label>
      {description && (
        <div className="description" id={descriptionId}>
          {description}
        </div>
      )}
      <select
        {...field}
        {...selectProps}
        onChange={handleChange}
        id={inputId}
        className={
          variant === 'outline'
            ? 'select-input select-input-outline'
            : 'select-input'
        }
        style={selectStyle}
        aria-describedby={describedBy}
        aria-invalid={shouldDisplayError || undefined}
        aria-errormessage={shouldDisplayError ? errorId : undefined}
      >
        {children}
      </select>
      {footer}
      {shouldDisplayError && (
        <div className="error" id={errorId} aria-live="polite">
          {meta.error}
        </div>
      )}
    </div>
  );
};

export const DateOfBirth = ({
  name,
  setFieldValue,
  question,
}: {
  name: string;
  setFieldValue: FormikHelpers<DateOfBirthValueType>['setFieldValue'];
  question?: string;
}) => {
  const t = useTranslateFunction('miscUI');
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const baseFieldName = name ?? inputId;
  const yearFieldName = `${baseFieldName}.year`;
  const monthFieldName = `${baseFieldName}.month`;
  const dayFieldName = `${baseFieldName}.day`;

  const [yearField, yearMeta] = useField({
    name: yearFieldName,
    id: yearFieldName,
    type: 'number',
  });
  const [monthField, monthMeta] = useField({
    name: monthFieldName,
    id: monthFieldName,
    type: 'number',
  });
  const [dayField, dayMeta] = useField({
    name: dayFieldName,
    id: dayFieldName,
    type: 'number',
  });
  // Whole-value errors (e.g. minimum-age) attach to the base field name
  // rather than to year/month/day individually.
  const [, dobMeta] = useField<DateOfBirthValueType>({name: baseFieldName});

  // Choose the first error that occurs
  const yearError =
    yearMeta.touched && yearMeta.error ? yearMeta.error : undefined;
  const monthError =
    !yearError && monthMeta.touched && monthMeta.error
      ? monthMeta.error
      : undefined;
  const dayError =
    !yearError && !monthError && dayMeta.touched && dayMeta.error
      ? dayMeta.error
      : undefined;
  const dobError =
    !yearError &&
    !monthError &&
    !dayError &&
    yearMeta.touched &&
    monthMeta.touched &&
    dayMeta.touched &&
    typeof dobMeta.error === 'string'
      ? dobMeta.error
      : undefined;
  const shouldDisplayError = !!(yearError || monthError || dayError || dobError);

  const dobFormStyle: CSSProperties = {
    maxWidth: '80px',
    marginRight: '0.5rem',
    border: '1px solid #e0e0e0',
    padding: '8px',
    borderRadius: '5px',
  };

  return (
    <fieldset className="question-spacing">
      <legend className="form-label">{question ?? t('What is your date of birth?')}</legend>
      <div className="d-flex">
        <div>
          <label htmlFor={yearFieldName}>{t('Year')}</label>
          <br />
          <input
            {...yearField}
            id={yearFieldName}
            name={yearFieldName}
            aria-invalid={!!yearError || undefined}
            aria-errormessage={yearError ? errorId : undefined}
            aria-describedby={yearError ? errorId : undefined}
            className="dark-placeholder"
            placeholder={t('YYYY', {forceString: true})}
            type="number"
            style={dobFormStyle}
            onInput={e => {
              const maxYear = new Date().getFullYear();
              const value = Number(e.currentTarget.value);
              if (value > maxYear) {
                setFieldValue(yearFieldName, maxYear);
              } else if (value < 0) {
                setFieldValue(yearFieldName, 0);
              }
            }}
          />
        </div>
        <div>
          <label htmlFor={monthFieldName}>{t('Month')}</label>
          <br />
          <input
            {...monthField}
            id={monthFieldName}
            name={monthFieldName}
            aria-invalid={!!monthError || undefined}
            aria-errormessage={monthError ? errorId : undefined}
            aria-describedby={monthError ? errorId : undefined}
            className="dark-placeholder"
            placeholder={t('MM', {forceString: true})}
            type="number"
            style={dobFormStyle}
            onInput={e => {
              const value = Number(e.currentTarget.value);
              if (value > 12) {
                setFieldValue(monthFieldName, 12);
              } else if (value < 0) {
                setFieldValue(monthFieldName, 0);
              }
            }}
          />
        </div>
        <div>
          <label htmlFor={dayFieldName}>{t('Day')}</label>
          <br />
          <input
            {...dayField}
            id={dayFieldName}
            name={dayFieldName}
            aria-invalid={!!dayError || undefined}
            aria-errormessage={dayError ? errorId : undefined}
            aria-describedby={dayError ? errorId : undefined}
            className="dark-placeholder"
            placeholder={t('DD', {forceString: true})}
            type="number"
            style={dobFormStyle}
            onInput={e => {
              const value = Number(e.currentTarget.value);
              if (value > 31) {
                setFieldValue(dayFieldName, 31);
              } else if (value < 0) {
                setFieldValue(dayFieldName, 0);
              }
            }}
          />
        </div>
      </div>
      {shouldDisplayError && (
        <div className="error" id={errorId}>
          {yearError || monthError || dayError || dobError}
        </div>
      )}
    </fieldset>
  );
};

export const NumericInput = ({
  name,
  label,
  min = 0,
  max = undefined,
  step = 1,
  description,
  placeholder,
  ...inputProps
}: {
  name: string;
  label: string;
  min?: number;
  max?: number;
  step?: number;
  description?: ReactNode;
  placeholder?: string;
} & InputHTMLAttributes<HTMLInputElement>) => {
  const [field, meta, helpers] = useField({
    name,
    type: 'number',
    ...inputProps,
  });
  const autoGeneratedId = useId();
  const inputId = inputProps.id ?? `${name}-${autoGeneratedId}`;
  const errorId = `${inputId}-error`;
  const descriptionId = `${inputId}-description`;
  const shouldDisplayError = meta.touched && !!meta.error;

  const describedBy =
    [
      description ? descriptionId : undefined,
      shouldDisplayError ? errorId : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  // Handle input change to ensure only numbers
  const handleInputChange: ChangeEventHandler<HTMLInputElement> = e => {
    const value = e.target.value;

    // Allow empty string, numbers, and negative sign
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      let inputValue = '';
      if (value !== '') {
        let numericValue = Number(value);

        // Apply min/max constraints if provided
        if (min !== undefined && numericValue < min) {
          numericValue = min;
        }
        if (max !== undefined && numericValue > max) {
          numericValue = max;
        }
        inputValue = String(numericValue);
      }
      helpers.setValue(inputValue);
    }
  };

  // Handle arrow key increments/decrements
  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();

      const currentValue = isNaN(Number(field.value)) ? 0 : Number(field.value);
      let newValue;

      if (e.key === 'ArrowUp') {
        newValue = currentValue + step;
      } else {
        newValue = currentValue - step;
      }

      // Apply min/max constraints
      if (min !== undefined && newValue < min) {
        newValue = min;
      }
      if (max !== undefined && newValue > max) {
        newValue = max;
      }

      helpers.setValue(newValue);
    }
  };

  return (
    <div className="question-spacing">
      <label className="form-label" htmlFor={inputId}>
        {label}
      </label>
      {description && (
        <div className="description" id={descriptionId}>
          {description}
        </div>
      )}

      <input
        {...field}
        {...inputProps}
        className="about-text-input dark-placeholder"
        type="number"
        inputMode="numeric"
        id={inputId}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={field.onBlur}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        aria-describedby={describedBy || undefined}
        aria-invalid={shouldDisplayError || undefined}
        aria-errormessage={shouldDisplayError ? errorId : undefined}
      />
      {shouldDisplayError && (
        <div className="error" id={errorId}>
          {meta.error}
        </div>
      )}
    </div>
  );
};
